import { data } from 'dcmjs';
import { cleanTags } from '@/lib/dicom/anonymizer';
import { OriginalAttributesSequenceTag } from '@/lib/dicom/DicomTags';
import { logger } from '@/util/logger';

const log = logger('DicomDictTransforms');

/**
 * The mutable surface of a parsed DICOM dictionary these transforms touch — the
 * tag dictionary and the tag-upsert method. `data.DicomDict` satisfies it, so
 * production callers pass the real dcmjs object while tests can use a plain
 * fixture. The generic `<T extends MutableDicomDict>` preserves the concrete
 * input type through the promise chain in DicomUtil.makeAnonymousDicomFile.
 */
export interface MutableDicomDict {
    dict: { [tagString: string]: data.DicomDictValue };
    upsertTag(tag: string, vr: string, values: unknown[]): void;
}

/**
 * Pure transforms applied to a parsed DICOM dictionary before it is
 * re-serialised. Each function mutates the dictionary in place and returns it so
 * they can be composed in a promise chain.
 */

/**
 * Anonymise a DICOM dictionary read with the dcmjs library.
 *
 * Note: this mutates the dictionary passed to it (it does not make a copy).
 */
export function anonymiseDicomData<T extends MutableDicomDict>(dicomData: T, caseId?: string): T {
    const dataDict: { [tagString: string]: data.DicomDictValue } = dicomData.dict;
    cleanTags(dataDict, caseId);
    // PatientIdentityRemoved
    dicomData.upsertTag('00120062', 'CS', ['YES']); // CS - stands for Code String (e.g: Yes or No values)
    // Anonymisation method
    dicomData.upsertTag('00120063', 'LO', ['Powered by Formus Labs']);
    return dicomData;
}

/**
 * Remove every element in private group 0x0009 before serialisation.
 *
 * Background: Zimmer Biomet CT scanners write private tags in group 0x0009
 * whose values violate the declared VR's length (e.g. a 32-char hex
 * identifier stored as VR=CS, max 16). dcmjs validates VR max-length on
 * write and throws. The app reads no group-0009 tag, so dropping them
 * is lossless from the application's perspective.
 */
export function stripPrivateGroup0009Tags<T extends MutableDicomDict>(dicomData: T): T {
    const dataDict: { [tagString: string]: data.DicomDictValue } = dicomData.dict;
    for (const tag of Object.keys(dataDict)) {
        if (tag.startsWith('0009')) {
            delete dataDict[tag];
        }
    }
    return dicomData;
}

/**
 * Modify the DICOM data dictionary and remove attributes that are known to be a
 * problem. The issues are known deviations from the DICOM standard.
 *
 * The DCMJS DICOM serialiser is reasonably rigid in enforcing that the fields are valid.
 *
 * The known problems are:
 *
 *   1. Date fields (vr=== 'DA') where the string is not of the form 'YYYYMMDD'
 *
 *     What we have seen is that equipment is putting dates in ISO-8601 style datetime strings. As
 *     an example "11/27/2019 12:47:52 AM". These strings have no time zone information.
 *
 *     Note: Instead of removing these fields, where they are well formed ISO-8601 datetime
 *     strings, the code could convert them to a simple date.
 */
export function removeKnownBadDicomData<T extends MutableDicomDict>(dicomData: T): T {
    const dataDict: { [tagString: string]: data.DicomDictValue } = dicomData.dict;

    for (const [tag, v] of Object.entries<data.DicomDictValue>(dataDict)) {
        if (v.vr === 'DA') {
            const value = v.Value;
            if (Array.isArray(value)) {
                if (
                    !value.every((item) => {
                        return typeof item === 'string' && item.length === 8;
                    })
                ) {
                    log.debug('remove DA tag %s, value %o', tag, value);
                    delete dataDict[tag];
                }
            } else if (typeof value === 'string' && value.length !== 8) {
                log.debug('remove DA tag %s, value %o', tag, value);
                delete dataDict[tag];
            } else {
                log.info('DA tag %s ignored, value %o', tag, value);
            }
        } else if (tag === OriginalAttributesSequenceTag && v.vr === 'SQ') {
            log.debug('OriginalAttributesSequence removed from DICOM');
            delete dataDict[tag];
        } // else not a 'DA' (date) type so ignore it.
    }
    return dicomData;
}
